import { ref, computed, onMounted, onUnmounted, Ref, ComputedRef } from 'vue'

const MOBILE_BREAKPOINT = 768

// Sidebar starts closed by default on every viewport — user opens it via the
// topbar toggle (and on desktop the toggle is the discoverable entry point to
// the navigation). `isMobile` is still seeded from the viewport at module load
// so `closeSidebar`/backdrop logic works before `onMounted` runs.
const initialIsMobile = typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT
const isMobile: Ref<boolean> = ref(initialIsMobile)
const isSidebarOpen: Ref<boolean> = ref(false)

const checkMobile = (): void => {
  const wasMobile = isMobile.value
  const isNowMobile = window.innerWidth < MOBILE_BREAKPOINT

  isMobile.value = isNowMobile

  if (wasMobile !== isNowMobile) {
    if (isNowMobile) {
      isSidebarOpen.value = false
    } else {
      isSidebarOpen.value = true
    }
  }
}

export interface UseSidebarReturn {
  isSidebarOpen: Ref<boolean>
  isMobile: ComputedRef<boolean>
  toggleSidebar: () => void
  openSidebar: () => void
  closeSidebar: () => void
}

export function useSidebar(): UseSidebarReturn {
  const toggleSidebar = (): void => {
    isSidebarOpen.value = !isSidebarOpen.value
  }

  const openSidebar = (): void => {
    isSidebarOpen.value = true
  }

  const closeSidebar = (): void => {
    isSidebarOpen.value = false
  }

  onMounted(() => {
    checkMobile()
    window.addEventListener('resize', checkMobile)
  })

  onUnmounted(() => {
    window.removeEventListener('resize', checkMobile)
  })

  return {
    isSidebarOpen,
    isMobile: computed(() => isMobile.value),
    toggleSidebar,
    openSidebar,
    closeSidebar
  }
}
